import { useEffect, useRef, useState, useCallback } from 'react';
import TouchKeypad from './TouchKeypad';

// Messages that get the typewriter treatment
function shouldTypewrite(msg) {
  if (!msg) return false;
  return msg.sender === 'device' || msg.sender === 'bes' || msg.sender === 'thoth' || msg.sender === 'swag' || msg.type === 'clunk';
}

// Extract display text from a message
function getMessageText(msg) {
  if (!msg) return '';
  if (msg.sender === 'device' || msg.sender === 'bes' || msg.sender === 'thoth' || msg.sender === 'swag') return msg.text || '';
  if (msg.type === 'clunk') return msg.text || '';
  return '';
}

// Speaker config: label, label color, text color
const SPEAKER_CONFIG = {
  device: { label: 'D.A.D.:', labelColor: '#34ff77', textColor: '#888' },
  bes:    { label: 'BES:',   labelColor: '#ffbd2e', textColor: '#cca050' },
  thoth:  { label: 'THOTH:', labelColor: '#7ae0ff', textColor: '#5ab0cc' },
  swag:   { label: 'SWAG:',  labelColor: '#f7931a', textColor: '#cc8822' },
};

// Speed tiers — clickable star controls
const SPEED_TIERS = [
  { emoji: '\u2726', ms: 120, label: 'ancient',   color: '#444' },
  { emoji: '\u2727', ms: 65,  label: 'steady',    color: '#ffbd2e' },
  { emoji: '\u2605', ms: 35,  label: 'swift',     color: '#34ff77' },
  { emoji: '\u2739', ms: 18,  label: 'blazing',   color: '#7ae0ff' },
  { emoji: '\u26A1', ms: 5,   label: 'lightning', color: '#fff' },
];

export default function RPGChat({ messages = [], onSubmit, inputDisabled, hintText, allowEmptySubmit, renderExportPanel, keyboardMode }) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  // --- Mobile detection ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 540);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 540);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // --- Scroll input into view on mobile keyboard open ---
  const handleInputFocus = useCallback(() => {
    if (!isMobile) return;
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 300);
  }, [isMobile]);

  // --- Speed tier state ---
  const [speedTierIndex, setSpeedTierIndex] = useState(0);
  const speedMsRef = useRef(SPEED_TIERS[0].ms);

  // --- Message queue + typewriter state ---
  const [viewIndex, setViewIndex] = useState(0);       // which message we're viewing
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [queueDone, setQueueDone] = useState(true);    // true when no auto-advance pending
  const [isPaused, setIsPaused] = useState(false);      // pause typewriter on tap

  // Track how many messages have been fully "processed" (typed out)
  const [processedCount, setProcessedCount] = useState(0);

  // Refs for stable access inside intervals/timeouts
  const messagesRef = useRef(messages);
  const processedCountRef = useRef(0);
  const isTypingRef = useRef(false);
  const viewIndexRef = useRef(0);
  const isPausedRef = useRef(false);
  const pausedCharRef = useRef(0);     // where we paused in the text
  const pausedMsgRef = useRef(null);   // which message was paused

  messagesRef.current = messages;

  // --- Detect new messages arriving ---
  const prevLengthRef = useRef(0);

  useEffect(() => {
    const prevLen = prevLengthRef.current;
    const newLen = messages.length;
    prevLengthRef.current = newLen;

    if (newLen > prevLen && prevLen > 0) {
      // New messages arrived -- start processing from the first new one
      // if we're not already behind
      if (processedCountRef.current >= prevLen) {
        // We were caught up, start typing the new batch
        setViewIndex(prevLen);
        viewIndexRef.current = prevLen;
        setQueueDone(false);
        setIsPaused(false);
        isPausedRef.current = false;
        startTyping(messages[prevLen]);
      } else {
        // Still processing earlier messages, the queue loop will pick these up
        setQueueDone(false);
      }
    } else if (prevLen === 0 && newLen > 0) {
      // First messages (boot)
      setViewIndex(0);
      viewIndexRef.current = 0;
      setProcessedCount(0);
      processedCountRef.current = 0;
      setQueueDone(false);
      startTyping(messages[0]);
    }
  }, [messages.length]);

  // --- Typewriter engine (recursive setTimeout for dynamic speed) ---
  const timeoutRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }, []);

  // Cleanup on unmount
  useEffect(() => clearTimers, [clearTimers]);

  const startTyping = useCallback((msg, startFrom = 0) => {
    clearTimers();

    if (!msg) {
      setQueueDone(true);
      return;
    }

    if (shouldTypewrite(msg)) {
      const fullText = getMessageText(msg);
      if (startFrom === 0) setDisplayedText('');
      setIsTyping(true);
      isTypingRef.current = true;

      let i = startFrom;
      const typeNextChar = () => {
        if (isPausedRef.current) {
          timeoutRef.current = setTimeout(typeNextChar, 50);
          return;
        }
        i++;
        setDisplayedText(fullText.slice(0, i));
        pausedCharRef.current = i;
        if (i >= fullText.length) {
          timeoutRef.current = null;
          setIsTyping(false);
          isTypingRef.current = false;
          onMessageDoneTyping();
        } else {
          timeoutRef.current = setTimeout(typeNextChar, speedMsRef.current);
        }
      };
      timeoutRef.current = setTimeout(typeNextChar, speedMsRef.current);
    } else {
      // Non-typewriter messages appear instantly
      setDisplayedText('');
      setIsTyping(false);
      isTypingRef.current = false;
      onMessageDoneTyping();
    }
  }, []);

  const onMessageDoneTyping = useCallback(() => {
    const idx = viewIndexRef.current;
    const newProcessed = idx + 1;
    setProcessedCount(newProcessed);
    processedCountRef.current = newProcessed;

    // Check if there's a next message to auto-advance to
    const msgs = messagesRef.current;
    if (newProcessed < msgs.length) {
      // Inter-message pause scales with speed tier
      const pauseMs = Math.max(50, speedMsRef.current * 6);
      timeoutRef.current = setTimeout(() => {
        const nextIdx = newProcessed;
        setViewIndex(nextIdx);
        viewIndexRef.current = nextIdx;
        startTyping(msgs[nextIdx]);
      }, pauseMs);
    } else {
      setQueueDone(true);
    }
  }, []);

  // --- Pause/resume on tap ---
  const handleMessageTap = useCallback(() => {
    if (!isTypingRef.current) return; // only meaningful during typing
    if (isPausedRef.current) {
      // Resume
      setIsPaused(false);
      isPausedRef.current = false;
    } else {
      // Pause
      setIsPaused(true);
      isPausedRef.current = true;
      pausedMsgRef.current = messagesRef.current[viewIndexRef.current];
    }
  }, []);

  // --- Navigation ---
  const canGoPrev = viewIndex > 0;
  const canGoNext = viewIndex < messages.length - 1;
  const hasNewBelow = viewIndex < processedCount - 1 || (viewIndex < messages.length - 1 && processedCount > viewIndex + 1);

  const goToPrev = useCallback(() => {
    if (!canGoPrev) return;
    clearTimers();
    setIsTyping(false);
    isTypingRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    const newIdx = viewIndex - 1;
    setViewIndex(newIdx);
    viewIndexRef.current = newIdx;
    setDisplayedText(''); // will show full text for already-processed messages
  }, [viewIndex, canGoPrev, clearTimers]);

  const goToNext = useCallback(() => {
    if (!canGoNext) return;
    clearTimers();
    setIsTyping(false);
    isTypingRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    const newIdx = viewIndex + 1;
    setViewIndex(newIdx);
    viewIndexRef.current = newIdx;

    // If this message hasn't been processed yet, start typing it
    if (newIdx >= processedCountRef.current) {
      setQueueDone(false);
      startTyping(messagesRef.current[newIdx]);
    } else {
      setDisplayedText('');
    }
  }, [viewIndex, canGoNext, clearTimers]);

  // --- Keyboard navigation (arrow keys when input is empty) ---
  const handleKeyDown = useCallback((e) => {
    if (input.trim() !== '') return; // only when input is empty
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      goToPrev();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      goToNext();
    }
  }, [input, goToPrev, goToNext]);

  const useTouchKeypad = isMobile && (keyboardMode === 'date' || keyboardMode === 'time' || keyboardMode === 'text') && !inputDisabled;

  // --- Auto-focus input when not disabled and queue is done ---
  // Skip auto-focus on mobile when in-app keypad is active (prevents native keyboard popup)
  useEffect(() => {
    if (!inputDisabled && queueDone && inputRef.current && !useTouchKeypad) {
      inputRef.current.focus();
    }
  }, [inputDisabled, queueDone, useTouchKeypad]);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (inputDisabled) return;
    if (!input.trim() && !allowEmptySubmit) return;
    onSubmit(input);
    setInput('');
  };

  // --- Render the current message ---
  const currentMsg = messages[viewIndex];
  const isProcessed = viewIndex < processedCount;

  const renderMessage = () => {
    if (!currentMsg) return null;

    // For processed messages being revisited, show full text
    if (isProcessed && !isTyping) {
      return renderFullMessage(currentMsg);
    }

    // For the actively-typing message
    if (isTyping && shouldTypewrite(currentMsg)) {
      return renderTypingMessage(currentMsg, displayedText);
    }

    // Instant messages (player, riddle, hint, system) or done typing
    return renderFullMessage(currentMsg);
  };

  const renderTypingMessage = (msg, text) => {
    const speaker = SPEAKER_CONFIG[msg.sender];
    if (speaker) {
      return (
        <div style={{ minHeight: 16 }}>
          <span style={{ color: speaker.labelColor }}>{speaker.label}</span>{' '}
          <span style={{ color: speaker.textColor }}>
            {text}
            {isPaused
              ? <span style={{ ...cursorStyle, color: '#ff5f56' }}>||</span>
              : <span style={cursorStyle}>_</span>
            }
          </span>
        </div>
      );
    }
    if (msg.type === 'clunk') {
      return (
        <div style={{ minHeight: 16 }}>
          <span style={{ color: msg.color || '#888' }}>&#9881; CLUNK.</span>{' '}
          <span style={{ color: '#888' }}>
            {text}
            {isPaused
              ? <span style={{ ...cursorStyle, color: '#ff5f56' }}>||</span>
              : <span style={cursorStyle}>_</span>
            }
          </span>
        </div>
      );
    }
    return null;
  };

  const renderFullMessage = (msg) => {
    const speaker = SPEAKER_CONFIG[msg.sender];
    if (speaker) {
      return (
        <div style={{ minHeight: 16 }}>
          <span style={{ color: speaker.labelColor }}>{speaker.label}</span>{' '}
          <span style={{ color: speaker.textColor }}>{msg.text}</span>
        </div>
      );
    }
    if (msg.type === 'celebration') {
      return (
        <div style={{
          minHeight: 16,
          animation: 'celebration-flash 0.8s ease-out',
          textAlign: 'center',
          color: '#00ff41',
          fontWeight: 'bold',
          letterSpacing: 2,
        }}>
          {msg.text}
        </div>
      );
    }
    if (msg.sender === 'player') {
      return (
        <div style={{ color: '#888', minHeight: 16 }}>
          <span style={{ color: '#555' }}>&gt; </span>
          {msg.text}
        </div>
      );
    }
    if (msg.type === 'riddle') {
      return (
        <div style={{
          border: '1px solid #222',
          padding: '4px 6px',
          background: 'rgba(140, 120, 255, 0.03)',
          minHeight: 16,
        }}>
          <div style={{ color: '#8b5cf6', marginBottom: 2 }}>RIDDLE:</div>
          <div style={{ color: '#aaa' }}>
            {msg.lines.map((line, li) => (
              <span key={li}>{line}{li < msg.lines.length - 1 ? <br /> : null}</span>
            ))}
          </div>
        </div>
      );
    }
    if (msg.type === 'clunk') {
      return (
        <div style={{ minHeight: 16 }}>
          <span style={{ color: msg.color || '#888' }}>&#9881; CLUNK.</span>{' '}
          <span style={{ color: '#888' }}>{msg.text}</span>
        </div>
      );
    }
    if (msg.type === 'hint') {
      return (
        <div style={{ color: '#444', fontSize: isMobile ? 9 : 7, minHeight: 12 }}>
          {msg.text}
        </div>
      );
    }
    if (msg.type === 'system') {
      return (
        <div style={{ color: '#333', fontSize: isMobile ? 9 : 7, minHeight: 12 }}>
          {msg.text}
        </div>
      );
    }
    if (msg.type === 'export-panel') {
      return renderExportPanel ? renderExportPanel() : null;
    }
    return null;
  };

  // Message counter
  const messageCounter = messages.length > 0
    ? `${viewIndex + 1}/${messages.length}`
    : '';

  return (
    <div style={{
      background: '#050508',
      padding: '6px 10px',
      fontSize: isMobile ? 10 : 9,
      lineHeight: 1.7,
      minHeight: 160,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTop: '2px solid #1a1a1a',
    }}>
      {/* Navigation + message display area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}>
        {/* Up arrow */}
        <div
          onClick={goToPrev}
          style={{
            color: canGoPrev ? '#666' : '#1a1a1a',
            cursor: canGoPrev ? 'pointer' : 'default',
            fontSize: 12,
            lineHeight: 1,
            userSelect: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 2,
            minHeight: isMobile ? 44 : 'auto',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => { if (canGoPrev) e.currentTarget.style.color = '#00ff41'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = canGoPrev ? '#666' : '#1a1a1a'; }}
        >
          <span>{canGoPrev ? '\u25B2' : '\u00A0'}</span>
          <span style={{ color: '#555', fontSize: isMobile ? 10 : 8 }}>{messageCounter}</span>
        </div>

        {/* Current message — click to pause/resume typewriter */}
        <div
          onClick={handleMessageTap}
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'flex-start',
            minHeight: 0,
            cursor: isTyping ? 'pointer' : 'default',
          }}
        >
          <div style={{
            width: '100%',
            overflowY: 'auto',
            maxHeight: '100%',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: 1.6,
          }}>
            {renderMessage()}
          </div>
        </div>

        {/* Down arrow */}
        <div
          onClick={() => {
            if (isPausedRef.current) {
              // Resume on down-arrow press while paused
              setIsPaused(false);
              isPausedRef.current = false;
            }
            goToNext();
          }}
          style={{
            color: canGoNext ? '#666' : '#1a1a1a',
            cursor: canGoNext ? 'pointer' : 'default',
            fontSize: 12,
            lineHeight: 1,
            userSelect: 'none',
            paddingTop: 2,
            minHeight: isMobile ? 44 : 'auto',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            transition: 'color 0.15s',
            animation: (canGoNext && hasNewBelow) ? 'arrow-pulse 2s ease-in-out infinite' : 'none',
          }}
          onMouseEnter={(e) => { if (canGoNext) e.currentTarget.style.color = '#00ff41'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = canGoNext ? '#666' : '#1a1a1a'; }}
        >
          {canGoNext ? '\u25BC' : '\u00A0'}
        </div>
      </div>

      {/* Speed star controls */}
      <div style={{ flexShrink: 0, padding: '2px 0 0 0' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          padding: '4px 0',
        }}>
          {SPEED_TIERS.map((tier, idx) => {
            const isActive = idx === speedTierIndex;
            return (
              <span
                key={tier.label}
                onClick={() => {
                  setSpeedTierIndex(idx);
                  speedMsRef.current = tier.ms;
                }}
                style={{
                  cursor: 'pointer',
                  fontSize: isMobile ? 18 : 14,
                  color: isActive ? tier.color : '#222',
                  textShadow: isActive ? `0 0 6px ${tier.color}` : 'none',
                  transition: 'color 0.2s, text-shadow 0.2s',
                  userSelect: 'none',
                  minWidth: isMobile ? 44 : 30,
                  minHeight: isMobile ? 44 : 'auto',
                  textAlign: 'center',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {tier.emoji}
              </span>
            );
          })}
        </div>
        <div style={{
          textAlign: 'center',
          color: '#333',
          fontSize: isMobile ? 9 : 7,
          lineHeight: 1,
          paddingBottom: 2,
        }}>
          {SPEED_TIERS[speedTierIndex].label}
        </div>
      </div>

      {/* Input line */}
      <form
        onSubmit={handleSubmit}
        style={{
          borderTop: '1px solid #1a1a1a',
          paddingTop: 4,
          marginTop: 2,
          display: 'flex',
          flexDirection: 'row',
          gap: 6,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ color: '#34ff77' }}>&gt;</span>
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => {
            if (useTouchKeypad) { e.target.blur(); return; }
            handleInputFocus();
          }}
          onPointerDown={(e) => { if (useTouchKeypad) e.preventDefault(); }}
          onTouchStart={(e) => { if (useTouchKeypad) e.preventDefault(); }}
          disabled={inputDisabled}
          placeholder={inputDisabled ? '' : (useTouchKeypad ? (keyboardMode === 'date' ? 'MM/DD/YYYY' : keyboardMode === 'time' ? 'H:MM AM/PM' : 'TYPE...') : 'TYPE YOUR RESPONSE')}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode={useTouchKeypad ? 'none' : undefined}
          readOnly={useTouchKeypad}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#aaa',
            caretColor: '#ffbd2e',
            fontFamily: 'inherit',
            fontSize: isMobile ? 16 : 9,
            minHeight: isMobile ? 44 : 'auto',
          }}
        />
      </form>

      {hintText && (
        <div style={{ color: '#444', fontSize: isMobile ? 9 : 7, marginTop: 2, flexShrink: 0 }}>
          {hintText}
        </div>
      )}

      {useTouchKeypad && (
        <TouchKeypad
          mode={keyboardMode}
          value={input}
          onChange={setInput}
          onSubmit={() => handleSubmit()}
        />
      )}
    </div>
  );
}

const cursorStyle = {
  color: '#ffbd2e',
  animation: 'blink 1s step-end infinite',
};
